import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bot, Loader, Loader2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';
import { processesQueryKey } from '../../queryKeys.ts';
import { useGenerateFlowchartMutation } from '../../queries/processes/generateFlowchart';
import { AiDocumentLibrary, PROCESS_FILES_READONLY_TOOLTIP } from './AiDocumentLibrary';
import { Tooltip, TooltipProvider } from '../../admin/components/shared/Tooltip.tsx';
import { ProcessDocumentUploadZone } from './ProcessDocumentUploadZone';
import {
  AI_LOADING_MESSAGES,
  PROCESS_FLOWCHART_SELECTION_CAP,
  PROCESS_LIBRARY_FILE_CAP,
  PROCESS_LIBRARY_VISIBLE_ROWS,
  PROCESS_MANUAL_CREATE_FILE_CAP
} from './constants';
import {
  extractProcessFileIds,
  getProcessDocumentsFromApi,
  initialFlowchartSelectedFileIds,
  mergeFlowchartSelectionAfterUpload,
  mergeLocalProcessDocumentsWithApi,
  parseGeneratedFlowchartVersion
} from './functional';
import {
  createPendingProcessDocument,
  isPendingProcessDocument,
  uploadPendingProcessDocuments
} from './processDocumentUpload.ts';
import { documentFileId, documentFileName, documentRowKey, documentSizeBytes } from '../../utils/documentUtils';
import { aiProcessDocumentFileSchema, aiProcessDocumentTagsSchema } from './schema';
import {
  currentPathWithSearch,
  mergeProcessNavigationState,
  soxProcessFlowchartPath,
} from '../../routing/processNavigation.ts';

function fileSignature(file) {
  return `${file?.name ?? ''}:${file?.size ?? 0}`;
}

function documentSignature(document) {
  return `${documentFileName(document)}:${documentSizeBytes(document)}`;
}

function documentNeedsFileId(document) {
  return !documentFileId(document) || isPendingProcessDocument(document) || document?.isUploading;
}

function isEmptyGeneratedFlowchart(data) {
  const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
  const connectors = Array.isArray(data?.connectors) ? data.connectors : [];
  return nodes.length === 0 && connectors.length === 0;
}

function isMinimalStartEndOnlyFlowchart(data) {
  const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
  const connectors = Array.isArray(data?.connectors) ? data.connectors : [];
  if (nodes.length !== 2 || connectors.length !== 1) {
    return false;
  }
  const nodeTypes = nodes.map((node) => String(node?.nodeType ?? '').toLowerCase());
  if (!nodeTypes.includes('start') || !nodeTypes.includes('end')) {
    return false;
  }
  return nodes.every((node) => {
    const nodeType = String(node?.nodeType ?? '').toLowerCase();
    return nodeType === 'start' || nodeType === 'end';
  });
}

function isNoRelevantGeneratedFlowchart(data) {
  return isEmptyGeneratedFlowchart(data) || isMinimalStartEndOnlyFlowchart(data);
}

const ProcessGenerateFlowchartPanel = forwardRef(function ProcessGenerateFlowchartPanel(
  {
    cycleId,
    processId,
    /** Route segment for flowchart URL / version queries (defaults to `processId`). */
    flowchartRouteProcessId,
    processName,
    description,
    initialProcess,
    documents: controlledDocuments,
    onDocumentsChange,
    selectedFileIds: controlledSelectedFileIds,
    onSelectedFileIdsChange,
    deferPersistence = false,
    onUploadingChange,
    onProcessFilesSynced,
    uploadDisabled = false,
    /** `card` — full section on process edit; `content` — library + upload only (modal body). */
    presentation = 'card',
    /** Manual create: upload/remove documents only (no flowchart selection or generate). */
    documentsOnly = false,
    /** Max linked documents (edit: 10; manual create: 5). */
    libraryFileCap = documentsOnly ? PROCESS_MANUAL_CREATE_FILE_CAP : PROCESS_LIBRARY_FILE_CAP,
    onGenerateSuccess,
    onGenerateUiStateChange,
    overlayZIndex = 200
  },
  ref
) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const generateFlowchartMutation = useGenerateFlowchartMutation();
  const generateAbortRef = useRef(null);
  /** Row keys removed while an upload batch is in flight (prevents stale upload results from re-adding rows). */
  const removedDuringUploadRef = useRef(new Set());

  const routeProcessId = String(flowchartRouteProcessId ?? processId ?? '').trim();

  const isDocumentsControlled = typeof onDocumentsChange === 'function';
  const isSelectionControlled = typeof onSelectedFileIdsChange === 'function';

  const [internalDocuments, setInternalDocuments] = useState(() => getProcessDocumentsFromApi(initialProcess));
  const [internalSelectedFileIds, setInternalSelectedFileIds] = useState(() =>
    initialFlowchartSelectedFileIds(
      getProcessDocumentsFromApi(initialProcess),
      PROCESS_FLOWCHART_SELECTION_CAP
    )
  );

  const documents = useMemo(() => {
    if (isDocumentsControlled) return controlledDocuments ?? [];
    return internalDocuments;
  }, [isDocumentsControlled, controlledDocuments, internalDocuments]);
  const setDocuments = isDocumentsControlled ? onDocumentsChange : setInternalDocuments;
  const selectedFileIds =
    isSelectionControlled && controlledSelectedFileIds instanceof Set
      ? controlledSelectedFileIds
      : internalSelectedFileIds;
  const setSelectedFileIds = isSelectionControlled ? onSelectedFileIdsChange : setInternalSelectedFileIds;

  const [activeTagDocumentId, setActiveTagDocumentId] = useState(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const isGenerating = generateFlowchartMutation.isPending;

  useEffect(() => {
    if (isDocumentsControlled) return;
    const apiDocuments = getProcessDocumentsFromApi(initialProcess);
    setDocuments((prev) => mergeLocalProcessDocumentsWithApi(prev, apiDocuments));
    setSelectedFileIds((prev) => {
      if (prev.size > 0) return prev;
      return initialFlowchartSelectedFileIds(apiDocuments, PROCESS_FLOWCHART_SELECTION_CAP);
    });
  }, [initialProcess, isDocumentsControlled, setDocuments, setSelectedFileIds]);

  useEffect(() => {
    if (!isGenerating) {
      setCurrentMessageIndex(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % AI_LOADING_MESSAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isGenerating]);

  const documentCount = documents.length;
  const linkedCount = useMemo(
    () => documents.filter((document) => Boolean(documentFileId(document))).length,
    [documents]
  );
  const uploadingCount = useMemo(
    () => documents.filter((document) => documentNeedsFileId(document)).length,
    [documents]
  );

  const selectedFileIdsList = useMemo(() => Array.from(selectedFileIds), [selectedFileIds]);

  const libraryDocuments = useMemo(
    () =>
      documents.map((document) => ({
        ...document,
        selected: Boolean(documentFileId(document) && selectedFileIds.has(documentFileId(document)))
      })),
    [documents, selectedFileIds]
  );

  const patchDocumentsMutation = useMutation({
    mutationFn: async (nextDocuments) => {
      if (!processId) {
        throw new Error('Process ID is missing');
      }
      const source = String(initialProcess?.source ?? 'manual').trim().toLowerCase();
      return axiosInstance.patch(API_ENDPOINTS.patchProcess(processId), {
        file_ids: extractProcessFileIds(nextDocuments),
        ...(source ? { source } : {})
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['process', processId] });
    },
    onError: (error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to link files to process')));
    }
  });

  const selectionLimitTooltip = `You can select up to ${PROCESS_FLOWCHART_SELECTION_CAP} documents for flowchart generation.`;

  /** PATCH process with linked file_ids (runs after each upload batch). */
  const syncProcessFiles = useCallback(
    async (nextDocuments) => {
      const readyDocuments = nextDocuments.filter((document) => documentFileId(document));
      if (readyDocuments.length === 0 || !processId) return;
      await patchDocumentsMutation.mutateAsync(nextDocuments);
      onProcessFilesSynced?.(nextDocuments);
    },
    [processId, patchDocumentsMutation, onProcessFilesSynced]
  );

  const applyUploadResultToDocuments = useCallback((prev, uploadResult, pendingDocs) => {
    const removed = removedDuringUploadRef.current;
    const pendingKeys = new Set(pendingDocs.map((doc) => documentRowKey(doc)));
    const uploadedByKey = new Map(
      uploadResult
        .filter((doc) => pendingKeys.has(documentRowKey(doc)))
        .map((doc) => [documentRowKey(doc), doc])
    );

    const reconciled = [];
    for (const doc of prev) {
      const key = documentRowKey(doc);
      if (removed.has(key)) continue;
      if (pendingKeys.has(key)) {
        const uploaded = uploadedByKey.get(key);
        if (uploaded && documentFileId(uploaded)) {
          reconciled.push(uploaded);
        }
        continue;
      }
      reconciled.push(doc);
    }

    for (const [key, uploaded] of uploadedByKey) {
      if (removed.has(key) || !documentFileId(uploaded)) continue;
      if (!reconciled.some((doc) => documentRowKey(doc) === key)) {
        reconciled.push(uploaded);
      }
    }

    return reconciled;
  }, []);

  const runUploadForDocuments = useCallback(
    async (mergedDocuments, pendingDocs) => {
      setIsUploadingFiles(true);
      try {
        const userHash = localStorage.getItem('userHash') || '';
        const next = await uploadPendingProcessDocuments(mergedDocuments, userHash);
        let reconciled = next;
        setDocuments((prev) => {
          reconciled = applyUploadResultToDocuments(prev, next, pendingDocs);
          return reconciled;
        });
        if (!uploadDisabled) {
          setSelectedFileIds((prev) => {
            const { nextSet, uploadedIds, selectionLimitReached } = mergeFlowchartSelectionAfterUpload(
              prev,
              pendingDocs,
              reconciled,
              PROCESS_FLOWCHART_SELECTION_CAP
            );
            if (selectionLimitReached) {
              toast.error(selectionLimitTooltip);
            }
            return uploadedIds.length > 0 ? nextSet : prev;
          });
        }
        await syncProcessFiles(reconciled);
        return reconciled;
      } catch (error) {
        console.error('Process document upload failed:', error);
        toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Upload failed')));
        setDocuments((prev) =>
          prev.filter((document) => {
            const key = documentRowKey(document);
            if (removedDuringUploadRef.current.has(key)) return false;
            return !pendingDocs.some((pending) => documentRowKey(pending) === key);
          })
        );
        throw error;
      } finally {
        pendingDocs.forEach((doc) => removedDuringUploadRef.current.delete(documentRowKey(doc)));
        setIsUploadingFiles(false);
      }
    },
    [
      applyUploadResultToDocuments,
      selectionLimitTooltip,
      setDocuments,
      setSelectedFileIds,
      syncProcessFiles,
      uploadDisabled
    ]
  );

  const uploadPendingDocuments = useCallback(async () => {
    const pending = documents.filter(isPendingProcessDocument);
    if (pending.length === 0) return documents;

    const pendingDocs = [...pending];
    const merged = documents.map((document) =>
      isPendingProcessDocument(document) ? { ...document, isUploading: true } : document
    );
    setDocuments(merged);
    return runUploadForDocuments(merged, pendingDocs);
  }, [documents, runUploadForDocuments, setDocuments]);

  useEffect(() => {
    onUploadingChange?.(
      isUploadingFiles ||
        documents.some((document) => document.isUploading || isPendingProcessDocument(document)) ||
        patchDocumentsMutation.isPending
    );
  }, [documents, isUploadingFiles, onUploadingChange, patchDocumentsMutation.isPending]);

  useEffect(() => {
    const linkedIds = new Set(
      documents.map((document) => documentFileId(document)).filter(Boolean)
    );
    setSelectedFileIds((prev) => {
      const pruned = [...prev].filter((id) => linkedIds.has(id));
      if (pruned.length === prev.size && pruned.every((id) => prev.has(id))) {
        return prev;
      }
      return new Set(pruned.slice(0, PROCESS_FLOWCHART_SELECTION_CAP));
    });
  }, [documents, setSelectedFileIds]);

  const removeDocument = useCallback(
    (rowKey) => {
      if (uploadDisabled) return;
      removedDuringUploadRef.current.add(rowKey);
      setDocuments((prev) => {
        const next = prev.filter((document) => documentRowKey(document) !== rowKey);
        if (!deferPersistence && processId) {
          void syncProcessFiles(next);
        }
        return next;
      });
    },
    [deferPersistence, processId, setDocuments, syncProcessFiles, uploadDisabled]
  );

  const toggleDocumentSelection = (rowKey, checked) => {
    if (uploadDisabled || documentsOnly) return;
    const doc = documents.find((document) => documentRowKey(document) === rowKey);
    const fileId = documentFileId(doc);
    if (!fileId) {
      toast.error('Wait for upload to finish and files to be linked to this process.');
      return;
    }
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        if (next.size >= PROCESS_FLOWCHART_SELECTION_CAP && !next.has(fileId)) {
          toast.error(selectionLimitTooltip);
          return prev;
        }
        next.add(fileId);
      } else {
        next.delete(fileId);
      }
      return next;
    });
  };

  const removeDocumentTag = (rowKey, tagToRemove) => {
    setDocuments((prev) => {
      const next = prev.map((document) =>
        documentRowKey(document) === rowKey
          ? { ...document, tags: (document.tags || []).filter((tag) => tag !== tagToRemove) }
          : document
      );
      if (!deferPersistence) {
        void syncProcessFiles(next);
      }
      return next;
    });
  };

  const closeTagCreator = () => {
    setActiveTagDocumentId(null);
  };

  const addDocumentTag = (rowKey, inputValue) => {
    const tag = String(inputValue || '').trim();
    if (!tag) return false;
    const currentDoc = documents.find((document) => documentRowKey(document) === rowKey);
    if (!currentDoc) return false;

    try {
      aiProcessDocumentTagsSchema.validateSync([...(currentDoc.tags || []), tag]);
    } catch (error) {
      toast.error(error?.message || 'Invalid tag');
      return false;
    }

    setDocuments((prev) => {
      const next = prev.map((document) =>
        documentRowKey(document) === rowKey
          ? { ...document, tags: [...(document.tags || []), tag] }
          : document
      );
      if (!deferPersistence) {
        void syncProcessFiles(next);
      }
      return next;
    });
    return true;
  };

  const addFilesToLibrary = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (!processId && !deferPersistence) return;

    const remainingSlots = libraryFileCap - documents.length;
    if (remainingSlots <= 0) {
      toast.error(`You can add up to ${libraryFileCap} files per process.`);
      return;
    }

    const existingSigs = new Set(documents.map((document) => documentSignature(document)));
    const rejectedMessages = new Map();
    const valid = [];
    const seenBatch = new Set();

    for (const file of files) {
      const batchSig = fileSignature(file);
      if (seenBatch.has(batchSig)) continue;
      seenBatch.add(batchSig);
      if (existingSigs.has(`${file.name}:${file.size}`)) {
        toast.error(`"${file.name}" is already in the list.`);
        continue;
      }
      try {
        aiProcessDocumentFileSchema.validateSync(file);
        valid.push(file);
      } catch (error) {
        const message = error?.message || 'File failed validation.';
        rejectedMessages.set(message, (rejectedMessages.get(message) || 0) + 1);
      }
    }

    const accepted = valid.slice(0, remainingSlots);
    rejectedMessages.forEach((count, message) => {
      toast.error(count > 1 ? `${message} (${count} files)` : message);
    });
    if (accepted.length === 0) return;

    const pendingDocs = accepted.map((file) => createPendingProcessDocument(file));
    const merged = [
      ...documents,
      ...pendingDocs.map((document) => ({ ...document, isUploading: true }))
    ];
    setDocuments(merged);

    try {
      await runUploadForDocuments(merged, pendingDocs);
    } catch {
      // runUploadForDocuments already toasts and rolls back failed rows
    }
  };

  const handleCancelGeneration = useCallback(() => {
    generateAbortRef.current?.abort();
    generateAbortRef.current = null;
  }, []);

  const handleGenerateFlowchart = useCallback(
    async ({ emptyChart = false } = {}) => {
      if (documents.some(documentNeedsFileId)) {
        toast.error('Wait until all files finish uploading and are linked to this process.');
        return;
      }
      if (patchDocumentsMutation.isPending || isUploadingFiles) {
        toast.error('Files are still being uploaded or saved to this process.');
        return;
      }
      const fileIds = emptyChart ? [] : selectedFileIdsList;
      if (!emptyChart && fileIds.length > PROCESS_FLOWCHART_SELECTION_CAP) {
        toast.error(selectionLimitTooltip);
        return;
      }

      generateAbortRef.current?.abort();
      const abortController = new AbortController();
      generateAbortRef.current = abortController;

      setGenerationError(null);

      const hasDescription = Boolean(String(description ?? '').trim());
      const hasSelectedDocuments = fileIds.length > 0;
      if (!emptyChart && !hasDescription && !hasSelectedDocuments) {
        toast(
          'No description or documents provided. Generated flowchart results may not be valid.',
          { icon: '⚠️' }
        );
      }

      try {
        const data = await generateFlowchartMutation.mutateAsync({
          cycleId,
          processId: routeProcessId || processId,
          processName,
          description,
          file_ids: fileIds,
          signal: abortController.signal
        });

        const isEmptyResult = !emptyChart && isNoRelevantGeneratedFlowchart(data);

        if (isEmptyResult) {
          toast.error('No relevant process found in the selected document(s) or description.');
        } else {
          toast.success(emptyChart ? 'Empty flowchart created' : 'Flowchart generated');
        }

        if (typeof onGenerateSuccess === 'function') {
          onGenerateSuccess(data);
          return;
        }

        const versionNumber = parseGeneratedFlowchartVersion(data);
        const flowchartPath =
          cycleId && routeProcessId ? soxProcessFlowchartPath(cycleId, routeProcessId) : null;

        if (flowchartPath) {
          navigate(flowchartPath, {
            state: mergeProcessNavigationState(
              {
                syncInitialPositions: true,
                ...(versionNumber != null ? { versionNumber } : {})
              },
              currentPathWithSearch(location)
            )
          });
        }

        void queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) });
        if (cycleId && routeProcessId) {
          // Prefix without auditYearId so every year-scoped versions query refreshes.
          void queryClient.invalidateQueries({
            queryKey: ['flowchart-versions', cycleId, routeProcessId]
          });
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        console.error('Generate flowchart failed:', error);
        const fallback = 'Failed to generate flowchart. Please try again.';
        const message =
          error?.code === 'ECONNABORTED'
            ? 'Flowchart generation timed out. Try again with fewer documents or check the AI service.'
            : resolve403ErrorMessage(error, getApiErrorMessage(error, fallback));
        setGenerationError(message);
      } finally {
        if (generateAbortRef.current === abortController) {
          generateAbortRef.current = null;
        }
      }
    },
    [
      cycleId,
      description,
      location,
      documents,
      generateFlowchartMutation,
      isUploadingFiles,
      navigate,
      onGenerateSuccess,
      patchDocumentsMutation.isPending,
      processId,
      processName,
      queryClient,
      routeProcessId,
      selectedFileIdsList,
      selectionLimitTooltip
    ]
  );

  const atDocumentCap = documentCount >= libraryFileCap;
  const filesBusy = isUploadingFiles || patchDocumentsMutation.isPending;
  const allFilesLinked = documentCount > 0 && documents.every((document) => documentFileId(document));
  const canAddMoreFiles =
    !uploadDisabled && !atDocumentCap && !isGenerating && !filesBusy;

  const selectionCount = selectedFileIdsList.length;
  const documentsReady =
    documentCount === 0 || (allFilesLinked && uploadingCount === 0);

  const generateDisabled =
    isGenerating ||
    filesBusy ||
    !documentsReady ||
    documents.some(documentNeedsFileId) ||
    selectionCount > PROCESS_FLOWCHART_SELECTION_CAP;

  const generateDisabledReason = filesBusy
    ? 'Uploading and saving files to this process…'
    : !documentsReady || documents.some(documentNeedsFileId)
      ? 'Waiting for all files to finish uploading'
      : null;

  const emptyChartDisabled =
    uploadDisabled || isGenerating || filesBusy || documents.some(documentNeedsFileId);

  const generateUiState = useMemo(
    () => ({
      generateDisabled,
      generateDisabledReason,
      emptyChartDisabled,
      isGenerating,
      selectionCount,
      documentCount,
      filesBusy,
      allFilesLinked,
      uploadingCount
    }),
    [
      generateDisabled,
      generateDisabledReason,
      emptyChartDisabled,
      isGenerating,
      selectionCount,
      documentCount,
      filesBusy,
      allFilesLinked,
      uploadingCount
    ]
  );

  useEffect(() => {
    onGenerateUiStateChange?.(generateUiState);
  }, [generateUiState, onGenerateUiStateChange]);

  useImperativeHandle(
    ref,
    () => ({
      uploadPendingDocuments,
      generateFlowchart: handleGenerateFlowchart,
      getGenerateUiState: () => generateUiState
    }),
    [uploadPendingDocuments, handleGenerateFlowchart, generateUiState]
  );

  const libraryMeta = documentsOnly
    ? `${linkedCount}/${libraryFileCap} linked${uploadingCount > 0 ? ` · ${uploadingCount} uploading` : ''}`
    : `${linkedCount}/${libraryFileCap} linked${
        uploadingCount > 0 ? ` · ${uploadingCount} uploading` : ''
      } · ${selectionCount}/${PROCESS_FLOWCHART_SELECTION_CAP} selected for flowchart`;

  const isContentPresentation = presentation === 'content';
  const showFlowchartActions = !documentsOnly && !isContentPresentation;
  const showDocumentSelection = !documentsOnly;

  const generateButton = (
    <button
      type="button"
      onClick={() => void handleGenerateFlowchart()}
      disabled={generateDisabled}
      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
      Generate flowchart
    </button>
  );

  const documentLibraryBlock = (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <AiDocumentLibrary
        documents={documentsOnly ? documents : libraryDocuments}
        activeTagDocumentId={activeTagDocumentId}
        onToggleDocumentSelection={toggleDocumentSelection}
        onRemoveDocument={removeDocument}
        onRemoveDocumentTag={removeDocumentTag}
        onOpenTagCreator={setActiveTagDocumentId}
        onCloseTagCreator={closeTagCreator}
        onAddDocumentTag={addDocumentTag}
        showRemove={documentsOnly}
        showTags={false}
        showFileSize
        showDocumentSelection={showDocumentSelection}
        interactionDisabled={uploadDisabled}
        interactionDisabledTooltip={PROCESS_FILES_READONLY_TOOLTIP}
        listTitle={isContentPresentation && !documentsOnly ? undefined : 'Process documents'}
        listMeta={isContentPresentation && !documentsOnly ? undefined : libraryMeta}
        selectionLimit={showDocumentSelection ? PROCESS_FLOWCHART_SELECTION_CAP : null}
        selectedCount={selectionCount}
        selectionLimitTooltip={selectionLimitTooltip}
        maxVisibleRows={PROCESS_LIBRARY_VISIBLE_ROWS}
        emptyMessage={
          uploadDisabled
            ? 'No process documents linked to this process.'
            : 'No process documents yet. Upload PDF or DOCX files below.'
        }
        hideOuterShell
      />

      {!uploadDisabled && !atDocumentCap ? (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
          <ProcessDocumentUploadZone
            variant="compact"
            maxFiles={libraryFileCap}
            disabled={!canAddMoreFiles}
            hiddenWhenDisabled={false}
            onFilesSelected={(fileList) => {
              void addFilesToLibrary(fileList);
            }}
          />
        </div>
      ) : null}

      {showFlowchartActions ? (
        <div className="flex flex-wrap items-center justify-start gap-2 border-t border-gray-100 px-4 py-3">
          {generateDisabledReason ? (
            <Tooltip content={generateDisabledReason} side="top">
              <span className="inline-flex">{generateButton}</span>
            </Tooltip>
          ) : (
            generateButton
          )}
        </div>
      ) : null}
    </div>
  );

  const showGenerationOverlay = isGenerating || Boolean(generationError);

  const generationOverlay = showGenerationOverlay ? (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ zIndex: overlayZIndex }}
      role="dialog"
      aria-modal="true"
      aria-busy={isGenerating}
    >
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-8 shadow-2xl">
        {generationError ? (
          <>
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-red-100 p-3">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <h3 className="mb-2 text-center text-xl font-semibold text-gray-900">
              Failed to Generate Flowchart
            </h3>
            <p className="mb-4 text-center text-sm text-gray-600">
              The AI service encountered an error. Please try again.
            </p>
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="font-mono text-sm text-red-800">{generationError}</p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setGenerationError(null)}
                className="flex-1 rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => void handleGenerateFlowchart()}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="rounded-full bg-purple-100 p-4">
                  <Bot className="h-12 w-12 animate-pulse text-purple-600" />
                </div>
                <div className="absolute -bottom-1 -right-1 rounded-full bg-white p-1 shadow-lg">
                  <Loader className="h-5 w-5 animate-spin text-purple-600" />
                </div>
              </div>
            </div>
            <h3 className="mb-2 text-center text-xl font-semibold text-gray-900">
              Skeptics AI is building your flowchart...
            </h3>
            <div className="mb-6 flex min-h-[60px] items-center justify-center">
              <p className="animate-fadeIn px-4 text-center text-sm text-gray-600">
                {AI_LOADING_MESSAGES[currentMessageIndex]}
              </p>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div className="animate-progress h-full rounded-full bg-gradient-to-r from-purple-500 to-blue-500" />
            </div>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleCancelGeneration}
                className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <TooltipProvider>
      {isContentPresentation && !documentsOnly ? (
        documentLibraryBlock
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <h2 className="text-base font-semibold text-gray-900">
              {documentsOnly ? 'Process documents' : 'Generate flowchart'}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {documentsOnly ? (
                <>
                  Add up to {libraryFileCap} PDF or DOCX files. Each file uploads when added; you can remove files while
                  uploading. Documents are linked when you create the process.
                </>
              ) : (
                <>
                  Add up to {libraryFileCap} documents; each file uploads immediately and is linked to this
                  process. Select up to {PROCESS_FLOWCHART_SELECTION_CAP} linked files for flowchart generation.
                  Selection is not saved when you update process details.
                </>
              )}
            </p>
          </div>
          <div className="p-6">{documentLibraryBlock}</div>
        </div>
      )}

      {typeof document !== 'undefined' && generationOverlay
        ? createPortal(generationOverlay, document.body)
        : null}
    </TooltipProvider>
  );
});

export default ProcessGenerateFlowchartPanel;
